import type { OutreachContactKind } from 'fym-shared/src/music24/outreach/types';
import type { ImportRow } from '../hooks/useImportContacts';

/** The most rows one import takes (music24-api `IMPORT_MAX_ROWS`). */
export const IMPORT_MAX_ROWS = 5000;

export type ImportField = 'name' | 'email' | 'kind' | 'url' | 'tags' | 'skip';

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/**
 * Parses CSV text (RFC 4180: quoted fields, doubled quotes, commas and new lines in quotes). A
 * semicolon file (European spreadsheets) is read too. Empty lines are dropped. Pure.
 */
export function parseCsv(text: string): ParsedCsv {
  const clean = text.replace(/^\uFEFF/, '');
  const firstLine = clean.split(/\r?\n/, 1)[0] ?? '';
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  for (let index = 0; index < clean.length; index += 1) {
    const char = clean[index];
    if (inQuotes) {
      if (char === '"' && clean[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      record.push(field.trim());
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && clean[index + 1] === '\n') {
        index += 1;
      }
      record.push(field.trim());
      records.push(record);
      record = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field !== '' || record.length > 0) {
    record.push(field.trim());
    records.push(record);
  }
  const nonEmpty = records.filter((row) => row.some((cell) => cell !== ''));
  const [headers = [], ...rows] = nonEmpty;
  return { headers, rows };
}

const GUESSES: Array<{ field: ImportField; pattern: RegExp }> = [
  { field: 'email', pattern: /e-?mail|address/i },
  { field: 'name', pattern: /name|outlet|curator|contact/i },
  { field: 'kind', pattern: /kind|type|category/i },
  { field: 'url', pattern: /url|link|website|site|web/i },
  { field: 'tags', pattern: /tag|label|group/i },
];

/** The field of each column, guessed from the header names (design outreach q3). One field one column. */
export function guessMapping(headers: string[]): ImportField[] {
  const used = new Set<ImportField>();
  return headers.map((header) => {
    const guess = GUESSES.find((candidate) => !used.has(candidate.field) && candidate.pattern.test(header));
    if (guess === undefined) {
      return 'skip';
    }
    used.add(guess.field);
    return guess.field;
  });
}

const KIND_WORDS: Array<{ kind: OutreachContactKind; pattern: RegExp }> = [
  { kind: 'outlet', pattern: /press|blog|magazine|newsletter|radio|podcast|outlet|media/i },
  { kind: 'creator', pattern: /creator|youtube|tiktok|instagram|influencer/i },
  { kind: 'curator', pattern: /playlist|curator/i },
];

function kindOf(value: string | undefined): OutreachContactKind | null {
  if (value === undefined || value.trim() === '') {
    return null;
  }
  return KIND_WORDS.find((entry) => entry.pattern.test(value))?.kind ?? null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface MappedImport {
  rows: ImportRow[];
  /** Rows with no valid email: the import skips them. */
  noEmail: number;
}

/** The import rows of a parsed file and its mapping. `extraTag` goes on every row. Pure. */
export function mapRows(csv: ParsedCsv, mapping: ImportField[], extraTag: string | null): MappedImport {
  const column = (field: ImportField) => mapping.indexOf(field);
  const emailAt = column('email');
  const nameAt = column('name');
  const kindAt = column('kind');
  const urlAt = column('url');
  const tagsAt = column('tags');
  let noEmail = 0;
  const rows: ImportRow[] = [];
  csv.rows.forEach((cells) => {
    const email = emailAt < 0 ? '' : (cells[emailAt] ?? '').trim().toLowerCase();
    if (!EMAIL.test(email)) {
      noEmail += 1;
      return;
    }
    const tags = [
      ...(tagsAt < 0 ? [] : (cells[tagsAt] ?? '').split(/[|,]/).map((tag) => tag.trim())),
      ...(extraTag === null ? [] : [extraTag]),
    ].filter((tag) => tag !== '');
    const url = urlAt < 0 ? '' : (cells[urlAt] ?? '').trim();
    rows.push({
      name: (nameAt < 0 ? '' : (cells[nameAt] ?? '').trim()) || email,
      email,
      kind: kindOf(kindAt < 0 ? undefined : cells[kindAt]),
      url: url === '' ? null : url,
      tags: tags.length === 0 ? null : [...new Set(tags)].slice(0, 20),
    });
  });
  return { rows, noEmail };
}
